import { html, LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { useStepperStore } from '../../stores/stepper.store.js';
import styles from './card-lint-selector.styles.js';

const CARD_INPUT_SELECTOR = 'input[type="radio"], textarea' as const;
const LINT_INPUT_SELECTOR = 'input[type="text"]' as const;

/**
 * @cssprop --y-cardlint-gap - The gap between the root items
 */
export class CardLintSelectorComponent extends LitElement {
  @property({ type: String, attribute: 'lint-product-id' })
  lintProductId = '';

  @state()
  private selectedOption: 'lint' | 'card' | undefined = undefined;

  protected override render(): unknown {
    return html`${this.renderHeaderSection()} ${this.renderBodySection()}`;
  }

  static override get styles() {
    return styles;
  }

  protected setLintSelected(): void {
    const lintSlot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="content-lint"]') ?? undefined;
    const cardSlot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="content-card"]') ?? undefined;
    if (cardSlot) {
      const els = cardSlot.assignedElements({ flatten: true });
      els.forEach((el) => {
        el.querySelectorAll<HTMLInputElement>(CARD_INPUT_SELECTOR).forEach((r) => {
          r.disabled = true;
        });
      });
    }
    if (lintSlot) {
      const els = lintSlot.assignedElements({ flatten: true });
      els.forEach((el) => {
        el.querySelectorAll<HTMLInputElement>(LINT_INPUT_SELECTOR).forEach((input) => {
          input.disabled = false;
        });
      });
    }

    useStepperStore().getState().extras.selectLint();
    useStepperStore().getState().extras.setLint(Number.parseInt(this.lintProductId, 10));

    this.selectedOption = 'lint';
  }

  protected setCardSelected(): void {
    const lintSlot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="content-lint"]') ?? undefined;
    const cardSlot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="content-card"]') ?? undefined;
    if (cardSlot) {
      const els = cardSlot.assignedElements({ flatten: true });
      els.forEach((el) => {
        el.querySelectorAll<HTMLInputElement>(CARD_INPUT_SELECTOR).forEach((r) => {
          r.disabled = false;
        });
      });
    }
    if (lintSlot) {
      const els = lintSlot.assignedElements({ flatten: true });
      els.forEach((el) => {
        el.querySelectorAll<HTMLInputElement>(LINT_INPUT_SELECTOR).forEach((input) => {
          input.disabled = true;
        });
      });
    }

    useStepperStore().getState().extras.selectCard();

    this.selectedOption = 'card';
  }

  /**
   * Renders the header section of the component. This header section contains
   * space for the user to add labels for the two toggle-able fields.
   *
   * The first label can be done using the slot named 'label-lint' and
   * the second label can be done using the slot named 'label-card'.
   */
  private renderHeaderSection() {
    return html`
      <header>
        <label @click="${this.setLintSelected}">
          <input
            name="card-lint"
            type="radio"
            value="lint"
            ?checked="${this.selectedOption === 'lint'}"
            @change="${this.setLintSelected}"
          />
          <slot name="label-lint"></slot>
        </label>

        <label @click="${this.setCardSelected}">
          <input
            name="card-lint"
            type="radio"
            value="card"
            ?checked="${this.selectedOption === 'card'}"
            @change="${this.setCardSelected}"
          />
          <slot name="label-card"></slot>
        </label>
      </header>
    `;
  }

  /**
   * Renders the body section of the component. This body contains space
   * for the user to add content for each of the two toggle-able fields.
   *
   * The first fieldset can be done using the slot named 'lint-content' and
   * the second fieldset can be done using the slot named 'card-content'.
   */
  private renderBodySection() {
    return html`
      <div ?aria-hidden="${this.selectedOption !== 'lint'}" ?hidden="${this.selectedOption !== 'lint'}">
        <slot name="content-lint"></slot>
      </div>
      <div ?aria-hidden="${this.selectedOption !== 'card'}" ?hidden="${this.selectedOption !== 'card'}">
        <slot name="content-card"></slot>
      </div>

      ${when(
        this.selectedOption === 'lint',
        () => html`
          <input type="hidden" value="1" name="${this.lintProductQuantityProperty}" />
          <input type="hidden" value="${this.lintProductId}" name="${this.lintProductIdProperty}" />
        `,
      )}
    `;
  }

  private get lintProductIdProperty(): string {
    return `items[${this.lintProductId}]id`;
  }

  private get lintProductQuantityProperty(): string {
    return `items[${this.lintProductId}]quantity`;
  }
}

export function defineCardLintSelectorComponent(): void {
  if (!customElements.get('y-card-lint-selector')) {
    customElements.define('y-card-lint-selector', CardLintSelectorComponent);
  }
}
